/** Lower case with accents removed, so "cafe" finds "café". */
export function fold(text: unknown): string {
  return String(text)
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase();
}

/**
 * A small in-memory index over every document. Every word of the query must
 * appear somewhere in the title, the file name or the text; title and name
 * matches come first.
 */
export class SearchIndex {
  /** @type {Map<string, { name: string, title: string, text: string }>} */
  #docs = new Map();

  /**
   * @param {string} path
   * @param {{ name?: string, title?: string, text?: string }} fields
   */
  set(path: string, { name = path, title = "", text = "" }) {
    this.#docs.set(path, { name: fold(name), title: fold(title), text: fold(text) });
  }

  /** @param {string} path */
  delete(path: string) {
    this.#docs.delete(path);
  }

  /** The paths whose title, name or text contain this fragment, for a quick first look. */
  mentions(fragment: string): string[] {
    const part = fold(fragment);
    const out: string[] = [];
    for (const [path, d] of this.#docs) {
      if (d.text.includes(part) || d.title.includes(part) || d.name.includes(part)) out.push(path);
    }
    return out;
  }

  clear() {
    this.#docs.clear();
  }

  /**
   * @param {string} query
   * @returns {string[]} matching paths, best first
   */
  search(query: string): string[] {
    const terms = fold(query).split(/\s+/).filter(Boolean);
    if (terms.length === 0) return [...this.#docs.keys()];

    const hits = [];
    for (const [path, d] of this.#docs) {
      let score = 0;
      let all = true;
      for (const term of terms) {
        if (d.title.includes(term)) score += 3;
        else if (d.name.includes(term)) score += 2;
        else if (d.text.includes(term)) score += 1;
        else {
          all = false;
          break;
        }
      }
      if (all) hits.push({ path, score });
    }
    return hits.sort((a, b) => b.score - a.score).map((h) => h.path);
  }
}
